import { storage } from './storage'

const _scopedStorage = storage.for('user')
const _OWNER_KEY = 'owner'
const _GENERATION_KEY = 'generation'
let _authenticatedGeneration: string | null = null
let _authenticatedOwner: string | null = null
const _listeners = new Set<() => void>()
const _notify = () => _listeners.forEach((listener) => listener())

/**
 * Browser data belonging to the authenticated user. Authentication establishes the
 * owner before protected routes mount. Old tabs may still hold a handle, so every
 * read and write checks ownership again. The generation changes after cleanup so
 * logging back in as the same user does not revive pre-logout handles. It is
 * captured when this tab authenticates, so remounting a page cannot renew access.
 */
export const userStorage = {
  /** Null when this tab's authenticated ownership has been invalidated. */
  sessionKey: (): string | null =>
    _authenticatedOwner !== null &&
    _authenticatedGeneration !== null &&
    _scopedStorage.get(_OWNER_KEY) === _authenticatedOwner &&
    _scopedStorage.get(_GENERATION_KEY) === _authenticatedGeneration
      ? JSON.stringify([_authenticatedOwner, _authenticatedGeneration])
      : null,
  /** Observe local authentication changes and invalidation from other tabs. */
  subscribe: (listener: () => void): (() => void) => {
    _listeners.add(listener)
    window.addEventListener('storage', listener)
    window.addEventListener('focus', listener)
    return () => {
      _listeners.delete(listener)
      window.removeEventListener('storage', listener)
      window.removeEventListener('focus', listener)
    }
  },
  activate: (subject: string): void => {
    if (
      _scopedStorage.get(_OWNER_KEY) !== subject ||
      _scopedStorage.get(_GENERATION_KEY) === null
    ) {
      _scopedStorage.clear()
      _scopedStorage.set(_GENERATION_KEY, crypto.randomUUID())
      _scopedStorage.set(_OWNER_KEY, subject)
    }
    _authenticatedGeneration = _scopedStorage.get(_GENERATION_KEY)
    _authenticatedOwner = subject
    _notify()
  },
  clear: (): void => {
    _authenticatedGeneration = null
    _authenticatedOwner = null
    _scopedStorage.clear()
    _notify()
  },
  for: (namespace: string, subject: string, customer: string) => {
    const generation = _authenticatedGeneration
    const isCurrentOwner = () =>
      generation !== null &&
      _scopedStorage.get(_OWNER_KEY) === subject &&
      _scopedStorage.get(_GENERATION_KEY) === generation
    const keyFor = (key: string) =>
      JSON.stringify([namespace, subject, customer, key])
    return {
      get: (key: string): string | null =>
        isCurrentOwner() ? _scopedStorage.get(keyFor(key)) : null,
      set: (key: string, value: string): void => {
        if (isCurrentOwner()) {
          _scopedStorage.set(keyFor(key), value)
        }
      },
    }
  },
}
